import React from 'react';
import { Link, browserHistory } from 'react-router';
import { mailMergeApi, outlookApi } from '../../requests/mailMerge';
import { readMailMergeSelection, writeMailMergeSelection } from '../../utils';
import {
    filterChips, tokenChipHtml, subjectTemplateText, blankTokensFor, blankListingTokens, isListingToken, tokenKeysUsed,
    tokenLabel, MODE_ALL, MODE_ONE, AUDIENCE_LENDERS, AUDIENCE_DOWNLOADERS, AUDIENCE_VENDORS, MAX_LISTINGS, tokensFor,
    isSinglePropertyAudience, isContactAudience,
} from './merge';
import { CONTACT_STARTER_BODY, templatesFor, templateById, templateTitle } from './templates';
import Composer from './Composer';
import Dropdown from './Dropdown';
import ListingPicker from './ListingPicker';
import Preview from './Preview';
import Recipients from './Recipients';
import RunConfirm from './SendConfirm';
import RunPanel from './RunPanel';

// Structure only — where the tokens go and nothing the broker has to delete.
// A sample letter here would be words nobody chose being sent to real owners.
const STARTER_BODY =
    '<p>' + tokenChipHtml('OWNER_FIRST_NAME', 'First name') + ',</p>' +
    '<p><br></p>' +
    '<p>' + tokenChipHtml('PROPERTY_LIST', 'Property list') + '</p>' +
    '<p><br></p>' +
    '<p>' + tokenChipHtml('AGENT_SIGNATURE', 'Agent signature') + '</p>';

// A loan opportunity to the lenders on one property's Loan Bids list.
const LENDER_STARTER_BODY =
    '<p>' + tokenChipHtml('LENDER_CONTACT_FIRST_NAME', 'First name') + ',</p>' +
    '<p>I hope all is well.</p>' +
    '<p>Peak Capital has been engaged to source financing for ' + tokenChipHtml('PROPERTY_NAME', 'Property') +
    ', located at ' + tokenChipHtml('PROPERTY_ADDRESS', 'Property address') + '.</p>' +
    '<p>The property is under contract at a purchase price of ' + tokenChipHtml('PURCHASE_PRICE', 'Price') +
    ', representing a ' + tokenChipHtml('CAP_RATE', 'Cap rate') + ' cap rate based on an NOI of ' +
    tokenChipHtml('NOI', 'NOI') + '.</p>' +
    '<p>Property Highlights:</p>' +
    '<p>' + tokenChipHtml('HIGHLIGHTS', 'Highlights') + '</p>' +
    '<p>Please click the link below to access the property’s marketing package: ' +
    tokenChipHtml('PROPERTY_LINK', 'Property link') + '</p>' +
    '<p>Assuming the borrower meets credit and underwriting requirements, would you be interested in financing this asset? ' +
    'If so, please provide an indication of your current rates &amp; terms, including max LTV, loan term, ' +
    'amortization, recourse requirements, and any applicable fees or prepayment restrictions.</p>' +
    '<p>If you need any additional information to evaluate the opportunity, please let me know.</p>' +
    '<p>Thanks, and I look forward to hearing from you.</p>' +
    '<p>Best,</p>' +
    '<p>' + tokenChipHtml('AGENT_NAME', 'Agent name') + '</p>';

// The follow-up the client wrote for the people who downloaded a property's
// marketing package (Claritask: "Adding Mail Merge To Downloads In Buckets").
const DOWNLOADER_STARTER_SUBJECT =
    tokenChipHtml('PROPERTY_NAME', 'Property') + ' - ' + tokenChipHtml('CITY_STATE', 'City, State');

const DOWNLOADER_STARTER_BODY =
    '<p>' + tokenChipHtml('CONTACT_FIRST_NAME', 'First name') + ',</p>' +
    '<p>Thank you for your interest in ' + tokenChipHtml('PROPERTY_NAME', 'Property') + '.</p>' +
    '<p>As a follow up, the basic info is as follows:</p>' +
    '<p>Price: ' + tokenChipHtml('PURCHASE_PRICE', 'Price') +
    '<br>NOI: ' + tokenChipHtml('NOI', 'NOI') +
    '<br>Cap Rate: ' + tokenChipHtml('CAP_RATE', 'Cap rate') + '</p>' +
    '<p>' + tokenChipHtml('HIGHLIGHTS', 'Property highlights') + '</p>' +
    '<p>Please click the following link to download the full marketing package: ' +
    tokenChipHtml('PROPERTY_LINK', 'Property link') + '</p>' +
    '<p>If you have any questions or further interest, please let me know.</p>' +
    '<p>I look forward to hearing your thoughts.</p>' +
    '<p>' + tokenChipHtml('AGENT_SIGNATURE', 'Agent signature') + '</p>';

const RUN_POLL_MS = 2000;

const EMPTY_COUNTS = { properties: 0, owners: 0, emailable: 0, no_email: 0, emailable_properties: 0 };

const CARD_SINGLE = 'single';
const CARD_MULTI = 'multi';

const toLookup = (ids) => (ids || []).reduce((found, id) => ({ ...found, [id]: true }), {});

const NO_TEMPLATE = '';

// The composer reads its body back through the browser's parser, so a seed is
// compared with it the same way.
const parsed = (html) => {
    const holder = document.createElement('div');
    holder.innerHTML = html;
    return holder.innerHTML;
};

export default function MailMerge() {
    const [selection] = React.useState(readMailMergeSelection);
    const isLenders = Boolean(selection && selection.audience === AUDIENCE_LENDERS);
    const isDownloaders = Boolean(selection && selection.audience === AUDIENCE_DOWNLOADERS);
    // Lenders and downloaders are each about one property: no picker, no
    // portfolio modes, no listings and no investors added by hand.
    const isSingleProperty = Boolean(selection && isSinglePropertyAudience(selection.audience));
    // Vendors and agents come straight off their lists: no property at all,
    // so no portfolio modes, no listings and no investors added by hand.
    const isContacts = Boolean(selection && isContactAudience(selection.audience));
    const isVendors = Boolean(selection && selection.audience === AUDIENCE_VENDORS);
    const noun = isLenders ? 'lender' : isDownloaders ? 'contact' : isContacts ? (isVendors ? 'vendor' : 'agent') : 'owner';
    // What an untouched letter for this audience starts as.
    const starterBody = isLenders ? LENDER_STARTER_BODY
        : isDownloaders ? DOWNLOADER_STARTER_BODY
            : isContacts ? CONTACT_STARTER_BODY : STARTER_BODY;
    const [recipients, setRecipients] = React.useState([]);
    const [counts, setCounts] = React.useState(EMPTY_COUNTS);
    const [sender, setSender] = React.useState(null);
    // Which blanks the server will let a broker type a value into.
    const [fillableTokens, setFillableTokens] = React.useState([]);
    const [loading, setLoading] = React.useState(Boolean(selection));
    // Reloading the recipients after an investor is added or removed; the
    // composer stays put, unlike the first load.
    const [refreshing, setRefreshing] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [notice, setNotice] = React.useState(null);
    const [busy, setBusy] = React.useState(false);

    // The composer owns both fields' DOM, so the draft lives in a ref and a
    // counter re-renders the preview — React re-rendering a contenteditable
    // moves the caret on every keystroke.
    const draftRef = React.useRef({
        subject: (selection && selection.subject) || (isDownloaders ? DOWNLOADER_STARTER_SUBJECT : ''),
        bodyHtml: (selection && selection.bodyHtml) || starterBody,
        attachments: (selection && selection.attachments) || [],
    });
    const [draftVersion, setDraftVersion] = React.useState(0);

    // Card key => which of its owners is showing.
    const [previewIdx, setPreviewIdx] = React.useState({});
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [run, setRun] = React.useState(null);

    // Which of the client's letters the draft started from, if any.
    const [templateId, setTemplateId] = React.useState((selection && selection.templateId) || null);
    const template = templateById(templateId);
    // A new key seeds the composer again: it reads its fields only once.
    const [composerKey, setComposerKey] = React.useState(0);

    // How much of an owner's portfolio the letter speaks about. 'all' is what
    // the page has always done; 'one' and 'summary' are the client asking not
    // to list eight addresses at a stranger. A letter about one property per
    // owner starts in 'one'.
    const [propertyMode, setPropertyMode] = React.useState(() => (template && template.propertyMode) || MODE_ALL);
    // Owner id => property id, only while propertyMode is 'one'.
    const [choices, setChoices] = React.useState({});
    // Owner id => token => the value typed where the record was blank.
    const [overrides, setOverrides] = React.useState({});
    // Owner id => true, for the owners the broker decided to leave out. Kept
    // with the draft: a reload that forgot them would email the one they skipped.
    const [excluded, setExcluded] = React.useState(() => toLookup(selection && selection.excluded));
    // Investors the broker searched for and added, beyond the list's owners.
    // Kept with the draft too.
    const [investorIds, setInvestorIds] = React.useState(() => (selection && selection.investorIds) || []);

    // The Summit listings a buyside letter markets, in the order picked, and
    // what was typed once where they are blank. Kept with the draft too.
    const [listingIds, setListingIds] = React.useState((selection && selection.listingIds) || []);
    const [listingOverrides, setListingOverrides] = React.useState((selection && selection.listingOverrides) || {});
    // Every listing a letter can market, fetched the first time a letter needs one.
    const [listingChoices, setListingChoices] = React.useState(null);
    const [listingError, setListingError] = React.useState(null);

    const propertyIds = (selection && selection.propertyIds) || [];
    const contactIds = (selection && selection.contactIds) || [];
    const listQuery = (selection && selection.query) || {};
    const backLink = isLenders
        ? { pathname: '/property/' + propertyIds[0] }
        : isDownloaders
            ? { pathname: '/property-statistics/' + propertyIds[0] }
            : isContacts
                ? { pathname: isVendors ? '/vendors' : '/agents', query: listQuery }
                : { pathname: '/list', query: listQuery };
    const backLabel = isLenders
        ? '← Back to the property'
        : isDownloaders
            ? '← Back to the downloads'
            : isContacts
                ? (isVendors ? '← Back to the vendors' : '← Back to the agents')
                : '← Back to the property list';

    const loadRecipients = (ids) => mailMergeApi.recipients(propertyIds, selection.audience, ids, contactIds)
        .then((envelope) => {
            setRecipients(envelope.data);
            setCounts(envelope.meta.counts);
            setSender(envelope.meta.sender);
            setFillableTokens(envelope.meta.fillable_tokens || []);
        });

    React.useEffect(() => {
        if (!selection) return;

        loadRecipients(investorIds)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
        // The property set is fixed for the life of the page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the draft with the set it belongs to, so a reload mid-letter loses
    // neither.
    React.useEffect(() => {
        if (!selection) return;
        writeMailMergeSelection({
            ...selection,
            ...draftRef.current,
            excluded: Object.keys(excluded),
            templateId,
            listingIds,
            listingOverrides,
            investorIds,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftVersion, excluded, templateId, listingIds, listingOverrides, investorIds]);

    React.useEffect(() => {
        if (!run || run.status !== 'running') return undefined;

        const timer = setInterval(() => {
            mailMergeApi.run(run.run_id).then(setRun).catch(() => {});
        }, RUN_POLL_MS);

        return () => clearInterval(timer);
    }, [run]);

    // In 'one property' mode an owner nobody has chosen for yet falls back to
    // their first property, so the letter is never ambiguous while the broker
    // works through them.
    const choiceFor = (recipient) => {
        if (propertyMode !== MODE_ONE) return null;
        const properties = recipient.properties || [];
        return choices[recipient.id] || (properties[0] && properties[0].id) || null;
    };

    // Reads the listing values worked out further down; nothing calls it before them.
    const optionsFor = (recipient) => ({
        choice: choiceFor(recipient),
        overrides: overrides[recipient.id] || {},
        mode: propertyMode,
        listings: pickedListings,
        listingOverrides: typedListing,
    });

    const emailable = recipients.filter((recipient) => recipient.email && !excluded[recipient.id]);
    const skipped = recipients.filter((recipient) => !recipient.email);
    const leftOut = recipients.filter((recipient) => recipient.email && excluded[recipient.id]);

    // The few owners who need a property picked get a card of their own, but
    // only when there are owners of both kinds: one kind alone reads as one card.
    const severalOwners = emailable.filter((recipient) => recipient.property_count > 1);
    const singleOwners = emailable.filter((recipient) => recipient.property_count <= 1);
    const split = severalOwners.length > 0 && singleOwners.length > 0;
    const cards = split
        ? [
            { key: CARD_SINGLE, title: 'Preview', owners: singleOwners },
            { key: CARD_MULTI, title: 'Multiple properties', owners: severalOwners },
        ]
        : [{ key: CARD_SINGLE, title: 'Preview', owners: emailable }];

    const indexIn = (card) => Math.min(previewIdx[card.key] || 0, Math.max(0, card.owners.length - 1));
    const shownIn = (card) => card.owners[indexIn(card)] || null;
    const shownIds = cards.map(shownIn).filter(Boolean).map((owner) => owner.id);

    // Recomputed whenever the draft changes, which is what draftVersion is for.
    const tokenKeys = React.useMemo(
        () => tokenKeysUsed(draftRef.current.subject, draftRef.current.bodyHtml),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [draftVersion],
    );

    const usesListing = !isSingleProperty && !isContacts && tokenKeys.some(isListingToken);
    const paletteTokens = tokensFor(selection && selection.audience);

    React.useEffect(() => {
        if (!usesListing || listingChoices || listingError) return;

        mailMergeApi.listings().then(setListingChoices).catch((err) => setListingError(err.message));
    }, [usesListing, listingChoices, listingError]);

    // What the letter markets: the picked listings the picker still offers.
    const pickedListings = React.useMemo(
        () => listingIds
            .map((id) => (listingChoices || []).filter((listing) => listing.id === id)[0])
            .filter(Boolean),
        [listingChoices, listingIds],
    );

    // What the picked listings leave blank, and what was typed for exactly those.
    const listingGaps = usesListing && pickedListings.length ? blankListingTokens(tokenKeys, pickedListings, {}) : [];
    const typedListing = {};
    listingGaps.forEach((token) => {
        const value = (listingOverrides[token] || '').trim();
        if (value) typedListing[token] = value;
    });
    const listingStillBlank = listingGaps.filter((token) => !typedListing[token]);
    const needsListing = usesListing && pickedListings.length === 0;

    const missingFor = (recipient) => (sender
        ? blankTokensFor(tokenKeys, recipient, sender, optionsFor(recipient))
        : []);

    const needsAttention = sender
        ? emailable.filter((recipient) => missingFor(recipient).length > 0)
        : [];

    const flash = (message) => {
        setNotice(message);
        setTimeout(() => setNotice(null), 6000);
    };

    // Coming back from Microsoft, the outcome arrives in the address bar. It
    // is cleared once read so a refresh does not repeat it.
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const connected = params.get('outlook');
        const failed = params.get('outlook_error');

        if (!connected && !failed) return;

        flash(failed || 'Outlook connected — merges now go out from ' + connected + '.');
        window.history.replaceState({}, '', window.location.pathname);
        // Read once, on the way back from Microsoft.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateDraft = (field) => (html) => {
        if (draftRef.current[field] === html) return;
        draftRef.current = { ...draftRef.current, [field]: html };
        setDraftVersion((version) => version + 1);
    };

    // Only a letter that is still the template or starter it began as is
    // replaced without asking.
    const chooseTemplate = (id) => {
        const next = templateById(id);
        if (next === template) return;

        const startedAs = template ? template.bodyHtml : starterBody;
        const edited = draftRef.current.bodyHtml !== parsed(startedAs);
        const replacement = next ? 'the ' + templateTitle(next) + ' template' : 'an empty letter';

        if (edited && !window.confirm('Replace the letter you have written with ' + replacement + '?')) return;

        draftRef.current = { ...draftRef.current, bodyHtml: next ? next.bodyHtml : starterBody };
        setTemplateId(next ? next.id : null);
        setPropertyMode((next && next.propertyMode) || MODE_ALL);
        setComposerKey((key) => key + 1);
        setDraftVersion((version) => version + 1);
    };

    // A typed value belongs to the listings it was typed for.
    const pickListing = (id) => {
        if (listingIds.indexOf(id) === -1 && pickedListings.length >= MAX_LISTINGS) {
            flash('At most ' + MAX_LISTINGS + ' listings fit in one letter.');
            return;
        }

        setListingIds((ids) => (ids.indexOf(id) === -1 ? ids.concat(id) : ids.filter((other) => other !== id)));
        setListingOverrides({});
    };

    const removeListing = (id) => {
        setListingIds((ids) => ids.filter((other) => other !== id));
        setListingOverrides({});
    };

    const typeListingValue = (token, value) =>
        setListingOverrides((current) => ({ ...current, [token]: value }));

    const chooseProperty = (ownerId, propertyId) =>
        setChoices((current) => ({ ...current, [ownerId]: propertyId }));

    const fillIn = (ownerId, token, value) =>
        setOverrides((current) => ({
            ...current,
            [ownerId]: { ...(current[ownerId] || {}), [token]: value },
        }));

    const toggleExcluded = (ownerId) =>
        setExcluded((current) => {
            const next = { ...current };
            if (next[ownerId]) delete next[ownerId];
            else next[ownerId] = true;
            return next;
        });

    // The server builds the added investors' rows, last-emailed date and all,
    // so the list is asked for again rather than patched here.
    const changeInvestors = (next, message) => {
        setRefreshing(true);
        loadRecipients(next)
            .then(() => {
                setInvestorIds(next);
                flash(message);
            })
            .catch((err) => flash(err.message))
            .finally(() => setRefreshing(false));
    };

    const addInvestors = (investors) => {
        const ids = investors.map((investor) => String(investor.ARCIMEDES_ROW_ID));
        const next = investorIds.concat(ids.filter((id) => investorIds.indexOf(id) === -1));
        changeInvestors(next, 'Added ' + ids.length + (ids.length === 1 ? ' investor.' : ' investors.'));
    };

    const removeAdded = (investorId) => {
        const row = recipients.filter((recipient) => recipient.id === investorId)[0];
        changeInvestors(
            investorIds.filter((id) => id !== investorId),
            'Removed ' + (row ? row.name : 'the investor') + ' from this merge.',
        );
    };

    const stepCard = (card, step) =>
        setPreviewIdx((current) => {
            const count = Math.max(1, card.owners.length);
            const at = Math.min(current[card.key] || 0, count - 1);
            return { ...current, [card.key]: (at + step + count) % count };
        });

    const showInPreview = (ownerId) => {
        const card = cards.filter((candidate) => candidate.owners.some((owner) => owner.id === ownerId))[0];
        if (!card) return;

        setPreviewIdx((current) => ({
            ...current,
            [card.key]: card.owners.findIndex((owner) => owner.id === ownerId),
        }));
    };

    const connectOutlook = () => {
        setBusy(true);
        outlookApi.authorizeUrl()
            .then((url) => { window.location.assign(url); })
            .catch((err) => { flash(err.message); setBusy(false); });
    };

    const disconnectOutlook = () => {
        setBusy(true);
        outlookApi.disconnect()
            .then(() => {
                setSender({ ...sender, connected: false, mailbox: null });
                flash('Outlook disconnected. Connect it again before the next merge.');
            })
            .catch((err) => flash(err.message))
            .finally(() => setBusy(false));
    };

    // Only the owners actually being written to carry a choice to the server;
    // in 'all properties' mode there is nothing to send.
    const chosenProperties = () => {
        const picked = {};
        emailable.forEach((recipient) => {
            const choice = choiceFor(recipient);
            if (choice) picked[recipient.id] = String(choice);
        });
        return picked;
    };

    const typedValues = () => {
        const filled = {};
        emailable.forEach((recipient) => {
            const values = overrides[recipient.id] || {};
            const kept = {};
            Object.keys(values).forEach((token) => {
                if (values[token] && values[token].trim()) kept[token] = values[token].trim();
            });
            if (Object.keys(kept).length) filled[recipient.id] = kept;
        });
        return filled;
    };

    const currentSelection = () => ({
        propertyIds,
        contactIds,
        audience: selection.audience,
        subject: draftRef.current.subject,
        bodyHtml: draftRef.current.bodyHtml,
        mode: propertyMode,
        choices: chosenProperties(),
        overrides: typedValues(),
        excluded: Object.keys(excluded),
        attachments: draftRef.current.attachments,
        listingIds: usesListing ? pickedListings.map((listing) => listing.id) : [],
        listingOverrides: usesListing ? typedListing : {},
        investorIds: isSingleProperty || isContacts ? [] : investorIds,
    });

    const sendTest = () => {
        setBusy(true);
        mailMergeApi.sendTest(currentSelection())
            .then((data) => flash('Test sent to ' + data.sent_to + ' using ' + data.built_from + "'s details. Nothing was sent to " + noun + "s."))
            .catch((err) => flash(err.message))
            .finally(() => setBusy(false));
    };

    const startSend = () => {
        setBusy(true);
        mailMergeApi.send(currentSelection())
            .then((data) => {
                setConfirmOpen(false);
                // The run now owns the files and lets go of them when it ends,
                // so the draft must not offer them to a later send.
                updateDraft('attachments')([]);
                setRun({ run_id: data.run_id, status: 'running', sent: 0, skipped: 0, failed: 0 });
            })
            .catch((err) => flash(err.message))
            .finally(() => setBusy(false));
    };

    if (!selection) {
        return (
            <div className="MailMerge">
                <div className="mm-container">
                    <h1 className="mm-title">Mail merge</h1>
                    <div className="mm-card mm-run">
                        <div className="mm-run__pad">
                            <div className="mm-run__note">
                                A mail merge starts from the property list, or the Vendors or Agents list. Open the list
                                you want, then choose Mail merge.
                            </div>
                            <div style={{ marginTop: 14 }}>
                                <button className="mm-btn" onClick={() => browserHistory.push('/list')}>Go to properties</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const chips = filterChips(listQuery);
    // Until the server has answered there are no numbers, and printing zeros
    // reads as "this list has no owners" rather than "not counted yet".
    const counted = !loading && !error;
    const stat = (value) => (counted ? Number(value).toLocaleString() : '—');
    const senderReady = sender && sender.email && sender.name;
    const outlookReady = sender && sender.connected;
    const nothingToSend = !loading && emailable.length === 0;
    const addedCount = recipients.filter((recipient) => recipient.added).length;
    const subjectEmpty = subjectTemplateText(draftRef.current.subject) === '';
    // How many properties the letters actually speak about — one each when the
    // broker has narrowed every owner down to a single property.
    // A lender or downloader letter is always about the one property, and a
    // vendor or agent letter about none.
    const previewProperties = isContacts ? null : isSingleProperty ? 1 : emailable.reduce(
        (total, recipient) => total + (choiceFor(recipient) ? 1 : recipient.property_count),
        0,
    );

    // Ordered by what the broker can act on next: finish the letter, then the
    // one-time Outlook connection once there is something to send.
    const sendLabel = nothingToSend
        ? 'Send — no recipients'
        : subjectEmpty
            ? 'Send — needs a subject'
            : needsListing
                ? 'Send — pick a listing'
                : !outlookReady
                    ? 'Send — connect Outlook first'
                    : 'Send to ' + emailable.length + (emailable.length === 1 ? ' ' + noun : ' ' + noun + 's');

    // The client's letters are for owners, vendors and agents; a lender or
    // downloader merge keeps its own letter.
    const setup = isSingleProperty ? null : (
        <React.Fragment>
            <div className="mm-field">
                <div className="mm-eyebrow">Template</div>
                <div className="mm-template">
                    <Dropdown
                        value={template ? templateTitle(template) : ''}
                        placeholder="None — write your own"
                        options={[{ key: NO_TEMPLATE, primary: 'None — write your own' }].concat(
                            templatesFor(selection.audience).map((entry) => ({ key: entry.id, primary: entry.name, group: entry.group })),
                        )}
                        isOn={(key) => key === (templateId || NO_TEMPLATE)}
                        onPick={(key) => chooseTemplate(key || null)}
                        ariaLabel="Template"
                    />
                </div>
            </div>
            {usesListing ? (
                <ListingPicker
                    choices={listingChoices}
                    error={listingError}
                    picked={pickedListings}
                    onPick={pickListing}
                    onRemove={removeListing}
                    gaps={listingGaps}
                    typed={listingOverrides}
                    onType={typeListingValue}
                />
            ) : null}
        </React.Fragment>
    );

    return (
        <div className="MailMerge">
            <div className="mm-container">
                <Link className="mm-back" to={backLink}>{backLabel}</Link>
                <h1 className="mm-title">Mail merge</h1>

                <div className="mm-card mm-source">
                    <div className="mm-source__filters">
                        {isSingleProperty ? (
                            <React.Fragment>
                                <div className="mm-eyebrow" style={{ marginRight: 4 }}>{isLenders ? 'Loan Bids' : 'Downloads'}</div>
                                <div className="mm-source__chip">{selection.propertyName}</div>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div className="mm-eyebrow" style={{ marginRight: 4 }}>
                                    {isContacts ? (isVendors ? 'From the Vendors list' : 'From the Agents list') : 'From property list'}
                                </div>
                                {chips.map((chip) => (
                                    <div className="mm-source__chip" key={chip.key}>
                                        <span>{chip.label}</span>{chip.value}
                                    </div>
                                ))}
                            </React.Fragment>
                        )}
                        <div className="mm-source__captured">
                            Captured {new Date(selection.capturedAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </div>
                    </div>
                    <div className={'mm-stats' + (isContacts ? ' mm-stats--three' : '')}>
                        {isContacts ? null : (
                            <div className="mm-stat">
                                <div className="mm-eyebrow">Properties</div>
                                <div className="mm-stat__value">{stat(counts.properties)}</div>
                            </div>
                        )}
                        <div className="mm-stat">
                            <div className="mm-eyebrow">{isLenders
                                ? 'Lenders'
                                : isDownloaders
                                    ? 'Downloaded by'
                                    : isContacts ? (isVendors ? 'Vendors' : 'Agents') : 'Unique owners'}</div>
                            <div className="mm-stat__value">{stat(counts.owners)}</div>
                            {counted && addedCount > 0
                                ? <div className="mm-stat__hint">{addedCount} added by you</div>
                                : null}
                        </div>
                        <div className="mm-stat">
                            <div className="mm-eyebrow">Will receive email</div>
                            <div className="mm-stat__value">
                                {stat(emailable.length)}
                                {counted && emailable.length > 0 ? <span className="mm-stat__dot"/> : null}
                            </div>
                            {counted && leftOut.length > 0
                                ? <div className="mm-stat__hint">{leftOut.length} left out by you</div>
                                : null}
                        </div>
                        <div className="mm-stat">
                            <div className="mm-eyebrow">No email on file</div>
                            <div className="mm-stat__value mm-stat__value--warn">{stat(counts.no_email)}</div>
                            {counted ? <div className="mm-stat__hint">not emailed</div> : null}
                        </div>
                    </div>
                </div>

                {sender && sender.test_redirect_to ? (
                    <div className="mm-alert--warn" style={{ margin: '14px 0 0' }}>
                        Test mode — every email goes to {sender.test_redirect_to} instead of the {noun}.
                        Clear MAIL_MERGE_TEST_TO to send for real.
                    </div>
                ) : null}

                {notice ? (
                    <div className="mm-notice">
                        <div>{notice}</div>
                        <button aria-label="Dismiss" onClick={() => setNotice(null)}>×</button>
                    </div>
                ) : null}

                {error ? <div className="mm-alert--danger" style={{ margin: '14px 0 0' }}>{error}</div> : null}

                {loading ? (
                    <div className="mm-card mm-run">
                        <div className="mm-run__pad"><div className="mm-run__note">{isLenders
                            ? 'Loading the Loan Bids list…'
                            : isDownloaders
                                ? 'Loading who downloaded the marketing package…'
                                : isContacts
                                    ? 'Loading the ' + noun + 's on this list…'
                                    : 'Working out who owns these properties…'}</div></div>
                    </div>
                ) : null}

                {!loading && !error && sender && !run ? (
                    <React.Fragment>
                        <div className="mm-columns">
                            <Composer
                                key={composerKey}
                                setup={setup}
                                subject={draftRef.current.subject}
                                onSubjectChange={updateDraft('subject')}
                                initialBodyHtml={draftRef.current.bodyHtml}
                                onBodyChange={updateDraft('bodyHtml')}
                                attachments={draftRef.current.attachments}
                                onAttachmentsChange={updateDraft('attachments')}
                                sender={sender}
                                disabled={nothingToSend || !senderReady || !outlookReady || subjectEmpty || needsListing || refreshing}
                                disabledReason={nothingToSend
                                    ? (isLenders
                                        ? 'No one left to email — add lenders with an email to the Loan Bids list, or put someone back in.'
                                        : isDownloaders
                                            ? 'No one left to email — nobody who downloaded this package has an email on file, or everyone was left out.'
                                            : isContacts
                                                ? 'No one left to email — go back to the list and change the filter, or put someone back in.'
                                                : 'No one left to email — go back to the list and change the selection, add investors, or put someone back in.')
                                    : !senderReady
                                        ? 'Your team member record needs a name and an email address before you can send.'
                                        : !sender.configured
                                            ? 'Outlook sending is not set up on this server yet, so nothing can be sent from here.'
                                            : !outlookReady
                                                ? 'Connect your Outlook mailbox to send — the email goes out from your own address and lands in your Sent Items.'
                                                : null}
                                onTest={sendTest}
                                onSend={() => setConfirmOpen(true)}
                                onConnect={connectOutlook}
                                onDisconnect={disconnectOutlook}
                                sendLabel={sendLabel}
                                propertyMode={propertyMode}
                                onPropertyMode={setPropertyMode}
                                busy={busy}
                                tokens={paletteTokens}
                                showPropertyModes={!isSingleProperty && !isContacts}
                            />
                            <div className={'mm-previews' + (split ? ' mm-previews--split' : '')}>
                                {cards.map((card) => {
                                    const shown = shownIn(card);

                                    return (
                                        <Preview
                                            key={card.key}
                                            title={card.title}
                                            className={card.key === CARD_MULTI ? 'mm-preview--multi' : ''}
                                            recipient={shown}
                                            index={indexIn(card)}
                                            total={card.owners.length}
                                            subject={draftRef.current.subject}
                                            bodyHtml={draftRef.current.bodyHtml}
                                            sender={sender}
                                            options={shown ? optionsFor(shown) : null}
                                            choosable={propertyMode === MODE_ONE}
                                            onChooseProperty={chooseProperty}
                                            showPropertyCount={!isContacts}
                                            onStep={(step) => stepCard(card, step)}
                                            onRemove={shown ? () => toggleExcluded(shown.id) : null}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        <Recipients
                            emailable={emailable}
                            skipped={skipped}
                            leftOut={leftOut}
                            needsAttention={needsAttention}
                            missingFor={missingFor}
                            overrides={overrides}
                            fillableTokens={fillableTokens}
                            activeIds={shownIds}
                            onPreview={showInPreview}
                            onFill={fillIn}
                            onToggleExcluded={toggleExcluded}
                            choosable={propertyMode === MODE_ONE}
                            choiceFor={choiceFor}
                            onChooseProperty={chooseProperty}
                            noun={noun}
                            onAddInvestors={isSingleProperty || isContacts ? null : addInvestors}
                            onRemoveAdded={removeAdded}
                            refreshing={refreshing}
                        />
                    </React.Fragment>
                ) : null}

                {run ? (
                    <RunPanel
                        run={run}
                        total={emailable.length}
                        backLink={backLink}
                        backLabel={backLabel}
                        noun={noun}
                    />
                ) : null}
            </div>

            {confirmOpen ? (
                <RunConfirm
                    owners={emailable.length}
                    noun={noun}
                    properties={previewProperties}
                    noEmail={counts.no_email}
                    leftOut={leftOut.length}
                    stillBlank={needsAttention.length}
                    listingBlanks={listingStillBlank.map(tokenLabel)}
                    subject={draftRef.current.subject}
                    sender={sender}
                    busy={busy}
                    onTest={sendTest}
                    onCancel={() => setConfirmOpen(false)}
                    onConfirm={startSend}
                />
            ) : null}
        </div>
    );
}
